import {
  AttachmentBadge,
  AttachmentPreview,
  AttachmentPreviewUtil,
  Badge,
  CondensedLabel,
  CondensedSmall,
  IconButton,
  Layout,
  LayoutItem,
  Loader,
  SlidingText,
  TilesItem,
  Well,
} from '@axiom/ui';
import React, { useState } from 'react';

import { chatFileDownloadEndpoint } from '../../api/chat-api';
import { EnvUtil } from '../../../utils/env-util';
import type {
  ChatAttachment,
  ChatMessageAttachment,
  ChatMessageEntry,
} from '../../types/chat.types';

import { useAttachmentPreviewData } from './useAttachmentPreviewData';
import { ChatAttachmentListModal } from './ChatAttachmentListModal';

const refusals = {
  error: {
    dot: 'red',
    title: (fileName: string) =>
      `${fileName} could not be uploaded - remove it or re-attach it`,
  },
  warning: {
    dot: 'yellow',
    title: (fileName: string) => `${fileName} could not be uploaded`,
  },
} as const;

export type ChatAttachmentListItemProps = { name: string } & (
  | {
      /** A file still on the composer's shelf: previewed from its own bytes, and removable. */
      attachment: ChatAttachment;
      chatId?: never;
      display?: never;
      /*
        The caller closes over which chip this is, rather than being handed the id back, so this
        component never has to know it is one of several.
      */
      onRemove: () => void;
    }
  | {
      /** A sent file: a download, inert until its upload has answered with an id. */
      attachment: ChatMessageAttachment;
      chatId: ChatMessageEntry['chatId'];
      /** `thumbnail` draws a picture large, opening it on click; anything else stays a row. */
      display?: 'thumbnail' | 'row';
      onRemove?: never;
    }
);

/**
 * ChatAttachmentListItem - I am one attached file, as a row or as a thumbnail. ONLY concerned with this
 * one file - the list decides which look each file gets.
 */
export const ChatAttachmentListItem = ({
  attachment,
  chatId,
  display = 'row',
  name,
  onRemove,
}: ChatAttachmentListItemProps) => {
  const [previewFailed, setPreviewFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const isShelved = 'file' in attachment;
  const fileName = isShelved ? attachment.file.name : attachment.fileName;

  /*
    A turn is drawn from the local `File`s before any upload has answered, so its files carry a name
    and no id yet - nothing to fetch or download. The ids arrive, or the send fails and the whole
    turn is taken back out of the log.
  */
  const isSaving = !isShelved && !attachment.fileId;

  const wantsThumbnail =
    !isShelved &&
    display === 'thumbnail' &&
    AttachmentPreviewUtil.isDisplayableImage({ fileName });

  // A sent row never fetches: a picture worth the bytes is a thumbnail, not a row.
  const { isLoading: isPreviewLoading, source: previewUrl } =
    useAttachmentPreviewData({
      attachment: isShelved || wantsThumbnail ? attachment : null,
      chatId,
    });

  // Both ids or nothing: the endpoint resolves a file against the chat it was uploaded to.
  const href =
    !isShelved && chatId && attachment.fileId
      ? `${EnvUtil.apiEnvoyUrl}${chatFileDownloadEndpoint({
          chatId,
          fileId: attachment.fileId,
        })}`
      : null;

  // After the hooks, so the early return does not change how many run.
  if (isSaving) {
    return (
      <TilesItem disabled name={name}>
        <Layout position="center middle">
          <Loader name={`${name}_SAVING`} type="dots" />
        </Layout>
      </TilesItem>
    );
  }

  /*
    A tile waits for its picture rather than falling through to the row, which would flash the file
    name and a download button for as long as the fetch takes, then swap them for the picture.
  */
  if (wantsThumbnail && isPreviewLoading) {
    return (
      <TilesItem disabled name={name}>
        <Layout position="center middle">
          <Loader name={`${name}_LOADING`} type="dots" />
        </Layout>
      </TilesItem>
    );
  }

  const picture = wantsThumbnail && !previewFailed ? previewUrl : null;

  /*
    Opens rather than downloads, and the picture it opens is the one already on screen - the hook
    holds the full-size bytes for as long as this tile does, so there is nothing further to fetch.
    Until they land, or if they never decode, the file is drawn as a row instead.
  */
  if (picture) {
    return (
      <>
        <TilesItem
          name={name}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <AttachmentPreview
            fileName={fileName}
            name="ATTACHMENT_PREVIEW"
            onError={() => {
              setPreviewFailed(true);
            }}
            size="large"
            src={picture}
            title={fileName}
          />
        </TilesItem>
        {isOpen ? (
          <ChatAttachmentListModal
            fileName={fileName}
            href={href}
            name={`${name}_MODAL`}
            onClose={() => {
              setIsOpen(false);
            }}
            previewUrl={picture}
          />
        ) : null}
      </>
    );
  }

  const rowPreview = (
    <AttachmentPreview
      fallback={
        <AttachmentBadge fileName={fileName} name="ATTACHMENT_EXTENSION" />
      }
      fileName={fileName}
      name="ATTACHMENT_PREVIEW"
      src={isShelved ? previewUrl : null}
    />
  );

  if (!isShelved) {
    return (
      <TilesItem disabled={!href} name={name}>
        <Layout horizontalGutter="12px" position="middle">
          <LayoutItem>{rowPreview}</LayoutItem>
          <LayoutItem fluid overflow="scroll">
            <CondensedLabel as="div" maxLines={2}>
              {fileName}
            </CondensedLabel>
          </LayoutItem>
          <IconButton
            disabled={!href}
            download={fileName}
            icon="download"
            name="DOWNLOAD_ATTACHMENT"
            pattern="secondary"
            size="small"
            title={`Download ${fileName}`}
            to={href}
            variation="minimal"
          />
        </Layout>
      </TilesItem>
    );
  }

  /*
    Marked rather than removed: the reader decides whether to press again or take it out. **The
    second refusal reads as an error**, not a caution - by then the file is the problem, and with
    several attached this is what says which one to remove.
  */
  const failures = attachment.uploadFailures ?? 0;
  const refusal = refusals[failures >= 2 ? 'error' : 'warning'];

  return (
    <Well
      border
      name={name}
      title={failures > 0 ? refusal.title(fileName) : undefined}
    >
      <Layout horizontalGutter="8px" position="middle">
        {failures > 0 ? (
          <Badge background={refusal.dot} minimal name="UPLOAD_REFUSED" />
        ) : null}
        {rowPreview}
        <SlidingText name="ATTACHMENT_NAME">
          <CondensedSmall as="span">{fileName}</CondensedSmall>
        </SlidingText>
        <IconButton
          size="small"
          icon="x-mark"
          name="REMOVE_ATTACHMENT"
          onClick={() => {
            onRemove?.();
          }}
          pattern="secondary"
          title={`Remove ${fileName}`}
          variation="minimal"
        />
      </Layout>
    </Well>
  );
};
